import { IDataContextProvider } from "@refinedev/core/dist/contexts/data/IDataContext";
import { AxiosInstance } from "axios";
import axAPI from "../../../api/axios-instance";
import { DataProvider } from "@refinedev/core";
import { dateFormat, generateFilter } from "../../../helpers";
import qs from "qs";
import { NewsCreateRequest } from "../Lib/types";
import dayjs from "dayjs";

/**
 * В форме мультиселект: "Все" (0) или все три лиги -> публикуем во все,
 * бэк ждёт для этого null. Иначе шлём список выбранных лиг (fnl-5624).
 */
const ALL_LEAGUE_IDS = [100, 200, 300];

function toLeaguePayload(value: unknown) {
  const selected = Array.isArray(value) ? value : value == null ? [] : [value];
  const leagues = selected
    .map(Number)
    .filter((id) => ALL_LEAGUE_IDS.includes(id));

  const isAll =
    selected.some((id) => Number(id) === 0) ||
    leagues.length === 0 ||
    ALL_LEAGUE_IDS.every((id) => leagues.includes(id));

  if (isAll) return { leagueId: null, leagueIds: null };
  if (leagues.length === 1) return { leagueId: leagues[0], leagueIds: leagues };
  return { leagueId: null, leagueIds: leagues };
}

/** Обратно в форму: null с обеих сторон — это "Все" (0). */
function toLeagueFormValue(response: {
  leagueIds?: number[] | null;
  leagueId?: number | null;
}) {
  if (response.leagueIds?.length) return response.leagueIds;
  if (response.leagueId) return [response.leagueId];
  return [0];
}

export const newsDataProvider = (
  apiUrl: string,
  httpClient: AxiosInstance = axAPI
): DataProvider =>
  <IDataContextProvider>{
    getList: async ({
      resource,
      pagination,
      filters,
      meta,
      hasPagination = true,
    }) => {
      const url = `/cms/${resource}`;
      const current = pagination?.current || 1;
      const pageSize = pagination?.pageSize || 10;

      const offset = pageSize * (current - 1);
      const limit = pageSize;

      const queryFilters = generateFilter(filters);

      const query = {
        ...(resource === "news"
          ? {
              limit,
              offset,
            }
          : {}),
      };

      try {
        const { data, headers } = await httpClient.get(
          `${url}?${qs.stringify({ ...query, ...queryFilters })}`
        );

        return {
          data: {
            news: data.news,
            mainNews: data.mainNews,
          },
          total: data.total,
        };
      } catch (e) {
        return Promise.reject(e);
      }
    },
    getOne: async ({ resource, id }) => {
      const url = "/cms/news/info";
      try {
        const { data: response } = await httpClient.get(
          `${url}?${qs.stringify({ id })}`
        );
        const data = await {
          ...response,
          leagueId: toLeagueFormValue(response),
          newsDate: dayjs(response.newsDate),
        };
        return {
          data,
        };
      } catch (error: any) {
        return Promise.reject(error);
      }
    },
    update: async ({
      resource,
      id,
      variables,
    }: {
      id: string;
      resource: string;
      variables: NewsCreateRequest;
    }) => {
      const url = "/cms/news/update";
      try {
        const { data } = await httpClient.post(url, {
          ...variables,
          ...toLeaguePayload(variables.leagueId),
          id,
          newsDate: dateFormat(
            variables.newsDate,
            "YYYY-MM-DDTHH:mm:ss.SSS[Z]"
          ),
          openForFnl: Boolean(variables.openForFnl),
          medicineNews: Boolean(variables.medicineNews),
        });

        return {
          data,
        };
      } catch (error: any) {
        return Promise.reject(error);
      }
    },
    create: async ({
      resource,
      variables,
    }: {
      resource: string;
      variables: NewsCreateRequest;
    }) => {
      const url = "cms/news/add";
      try {
        await httpClient.post(url, {
          ...variables,
          ...toLeaguePayload(variables.leagueId),
          hashTags: variables.hashTags || [],
          preview: variables.preview || "",
          newsDate: dateFormat(
            variables.newsDate,
            "YYYY-MM-DDTHH:mm:ss.SSS[Z]"
          ),
          openForFnl: Boolean(variables.openForFnl),
          medicineNews: Boolean(variables.medicineNews),
        });

        return Promise.resolve();
      } catch (error: any) {
        return Promise.reject(error);
      }
    },
    deleteOne: async ({
      resource,
      id,
      meta,
    }: {
      resource: string;
      id: number;
      meta: {
        id: number;
      };
    }) => {
      const url = "/cms/news/remove";
      try {
        const { data } = await httpClient.post(
          `${url}?${qs.stringify({ id: meta.id })}`
        );
        return {
          data,
        };
      } catch (error: any) {
        return Promise.reject(error);
      }
    },
  };
